'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

function ObjectModifyImage({
    src,
    alt,
    label,
    className,
}: Readonly<{
    src: string;
    alt: string;
    label: string;
    className: string;
}>) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        setImageLoaded(false);
    }, [src]);

    useEffect(() => {
        const image = imageRef.current;
        if (!image) {
            return;
        }

        if (image.complete && image.naturalWidth > 0) {
            setImageLoaded(true);
        }
    }, [src]);

    return (
        <div className={className}>
            {!imageLoaded && (
                <div className="absolute inset-0 z-10 overflow-hidden rounded-[16px] bg-white/5 before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent" />
            )}
            <Image
                ref={imageRef}
                src={src}
                alt={alt}
                fill
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
                className={
                    imageLoaded ? 'object-cover' : 'object-cover opacity-0'
                }
            />
            <div className="tablet:rounded-[8px] j-l1 absolute left-[8px] top-[8px] z-20 rounded-[4px] bg-[#4a4a4a]/80 px-[8px] py-[4px] text-white backdrop-blur-sm">
                {label}
            </div>
        </div>
    );
}

export default function ObjectModify() {
    const { t } = useTranslation('object-modify');

    return (
        <section className="tablet:pt-[80px] desktop:pt-[100px] tablet:pb-[120px] desktop:pb-[160px] relative z-10 flex w-full flex-col items-center bg-black pb-[80px] pt-[60px] text-white">
            <div className="tablet:max-w-[87.5vw] desktop:max-w-[1160px] tablet:gap-[32px] desktop:gap-[48px] flex w-[87.5vw] max-w-[480px] flex-col gap-[24px]">
                <div className="tablet:text-center j-h4 tablet:j-h3 text-start">
                    <p>{t('title.part1')}</p>
                    <p>
                        <span className="bg-[linear-gradient(90deg,#F7A1FF,#7AAFFF)] bg-clip-text text-transparent">
                            {t('title.part2')}
                        </span>
                    </p>
                </div>

                <div className="flex w-full flex-col items-start gap-[8px] md:flex-row">
                    {/* Before Image (Left) */}
                    <ObjectModifyImage
                        src={getStaticAssetUrl(
                            '/home/img-object-modify-1.webp',
                        )}
                        alt="Before"
                        label={t('image.before')}
                        className="tablet:w-[35%] relative aspect-[754/403] w-full overflow-hidden rounded-[4px] border border-white/20"
                    />

                    {/* After Image (Right) */}
                    <div className="tablet:w-[65%] flex w-full flex-col">
                        <ObjectModifyImage
                            src={getStaticAssetUrl(
                                '/home/img-object-modify-2.webp',
                            )}
                            alt="After"
                            label={t('image.after')}
                            className="relative aspect-[754/403] w-full overflow-hidden rounded-[4px] border border-white/20"
                        />
                        <p className="tablet:pt-[16px] j-t2 pt-[8px] text-left text-[#CCCBCE]">
                            Prompt: “add a glass and place it on magzine”
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
